import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import { unescapeName } from "Stores/agentsData";

/**
 * Two-panel sequence picker (folders on the left, sequences on the right) —
 * mirrors the "Add to Sequence" popup on Contacts. Loads the whole pickable set
 * in ONE fast call (/cm/seq/lite — id/name/folder only, no steps or stats) and
 * groups by folder client-side, so switching folders and searching are instant.
 */
const PINNED = ["My Sequences", "Shared with me"];

const SequencePickerModal = observer(({ open, currentId, onSelect, onClose }) => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!open) return;
    setChosen(null);
    setQuery("");
    (async () => {
      setLoading(true);
      const res = await makeApi({ url: URLS.sequencesLite, method: "GET" });
      const list = res && Array.isArray(res.data) ? res.data : [];
      setAll(list);
      setLoading(false);
    })();
  }, [open]);

  // Folder list derived from the data: pinned folders first, then the rest A→Z.
  const folders = useMemo(() => {
    const names = Array.from(new Set(all.map(s => s.folderName || "My Sequences")));
    const pinned = PINNED.filter(p => names.includes(p));
    const rest = names.filter(n => !PINNED.includes(n)).sort((a, b) => a.localeCompare(b));
    return [...pinned, ...rest];
  }, [all]);

  // Default the active folder once folders are known.
  useEffect(() => {
    if (open && folders.length && (!activeFolder || !folders.includes(activeFolder))) {
      setActiveFolder(folders[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, open]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const inFolder = all.filter(s => (s.folderName || "My Sequences") === activeFolder);
  const filtered = q ? inFolder.filter(s => String(s.name || "").toLowerCase().includes(q)) : inFolder;
  const counts = folder => all.filter(s => (s.folderName || "My Sequences") === folder).length;

  return (
    <div className="ag-seqm__overlay" onMouseDown={onClose}>
      <div className="ag-seqm" onMouseDown={e => e.stopPropagation()}>
        <div className="ag-seqm__head">
          <h3>Choose a sequence</h3>
          <button className="ag-seqm__close" onClick={onClose}>×</button>
        </div>

        <div className="ag-seqm__body">
          <div className="ag-seqm__folders">
            {loading && folders.length === 0 && <div className="ag-seqm__empty">Loading…</div>}
            {folders.map(f => (
              <div
                key={f}
                className={`ag-seqm__folder ${activeFolder === f ? "is-active" : ""}`}
                onClick={() => { setActiveFolder(f); setQuery(""); }}
              >
                <span className="ag-seqm__folderIcon">🗂</span>
                <span className="ag-seqm__folderName">{f}</span>
                <span className="ag-seqm__count">{counts(f)}</span>
              </div>
            ))}
          </div>

          <div className="ag-seqm__seqs">
            <input
              className="ag-input ag-seqm__search"
              placeholder="Search in this folder…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <div className="ag-seqm__list">
              {loading && <div className="ag-seqm__empty">Loading…</div>}
              {!loading && filtered.length === 0 && <div className="ag-seqm__empty">No sequences in this folder</div>}
              {!loading &&
                filtered.map(s => {
                  const id = s.id || s._id;
                  const label = unescapeName(s.name);
                  const active = (chosen ? chosen.id : currentId) === id;
                  return (
                    <label key={id} className={`ag-seqm__opt ${active ? "is-sel" : ""}`}>
                      <input
                        type="radio"
                        name="ag-seq"
                        checked={active}
                        onChange={() => setChosen({ id, name: label })}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="ag-seqm__foot">
          <button className="ag-btn" onClick={onClose}><i className="material-icons-outlined">close</i>Cancel</button>
          <button
            className="ag-btn ag-btn--primary"
            disabled={!chosen}
            onClick={() => { if (chosen) { onSelect(chosen); onClose(); } }}
          ><i className="material-icons-outlined">check</i>
            Select sequence
          </button>
        </div>
      </div>
    </div>
  );
});

export default SequencePickerModal;
